import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  initialAccounts, initialCards, initialCategories,
  initialTransactions, initialGoals, initialBudgets
} from '../utils/initialData';
import { loadLicense, isLicenseExpired, PLAN_LABEL } from '../utils/license';
import { setPrivacyMode } from '../utils/formatters';
import { loadSubscription, saveCloudData, subscribeCloudTransactions } from '../utils/cloudSync';
import { auth } from '../firebase';
import {
  demoAccounts, demoCards, demoTransactions, demoGoals,
  demoDebts, demoBudgets, demoInvestments, demoInvestHistory, demoUser,
  demoVehicles, demoVehicleMaintenance,
} from '../utils/demoData';
import { getCachedUsdRate, fetchUsdRate, toBRL, sumAccountsBRL } from '../utils/exchange';

export const IS_DEMO = new URLSearchParams(window.location.search).get('demo') === 'true';

const round2 = v => Math.round(v * 100) / 100;

// Aplica subcategorias padrão em categorias que ainda estão sem subcategorias
const defaultSubsMap = Object.fromEntries(
  initialCategories.map(c => [c.id, c.subcategories || []])
);
function migrateCategories(categories) {
  return categories.map(cat => {
    const hasSubs = cat.subcategories && cat.subcategories.length > 0;
    if (!hasSubs && defaultSubsMap[cat.id]?.length) {
      return { ...cat, subcategories: defaultSubsMap[cat.id] };
    }
    return cat;
  });
}

// Migração única: remove categoria "Bancário" duplicada, reassigna para "Contas"
function migrateRemoveBancario(categories, transactions, budgets) {
  const bancario = categories.find(c => /^banc[aá]rio$/i.test(c.name));
  if (!bancario) return { categories, transactions, budgets };
  return {
    categories:   categories.filter(c => c.id !== bancario.id),
    transactions: transactions.map(t => t.categoryId === bancario.id ? { ...t, categoryId: 'contas' } : t),
    budgets:      budgets.map(b => b.categoryId === bancario.id ? { ...b, categoryId: 'contas' } : b),
  };
}

function applyTxToBalance(accounts, tx, sign) {
  if (!tx.accountId || tx.status !== 'paid') return accounts;
  const delta = (tx.type === 'income' ? tx.value : -tx.value) * sign;
  return accounts.map(a => a.id === tx.accountId ? { ...a, balance: round2(a.balance + delta) } : a);
}

function applyTxToCard(cards, tx, sign) {
  if (!tx.cardId || tx.type !== 'expense') return cards;
  return cards.map(c => c.id === tx.cardId ? { ...c, used: Math.max(0, round2((c.used || 0) + tx.value * sign)) } : c);
}

export const APP_VERSION = '1.16.0';

const AppContext = createContext();

const load = (key, fallback) => {
  try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : fallback; }
  catch { return fallback; }
};

const _rawCats  = IS_DEMO ? initialCategories : migrateCategories(load('maw_categories', initialCategories));
const _rawTxs   = IS_DEMO ? demoTransactions  : load('maw_transactions', initialTransactions);
const _rawBudgs = IS_DEMO ? demoBudgets       : load('maw_budgets', initialBudgets);
const { categories: _initCats, transactions: _initTxs, budgets: _initBudgs } =
  IS_DEMO ? { categories: _rawCats, transactions: _rawTxs, budgets: _rawBudgs }
          : migrateRemoveBancario(_rawCats, _rawTxs, _rawBudgs);

const initialState = {
  accounts:            IS_DEMO ? demoAccounts      : load('maw_accounts',             initialAccounts),
  cards:               IS_DEMO ? demoCards         : load('maw_cards',                initialCards),
  categories:          _initCats,
  transactions:        _initTxs,
  transfers:           IS_DEMO ? []                : load('maw_transfers',            []),
  goals:               IS_DEMO ? demoGoals         : load('maw_goals',                initialGoals),
  budgets:             _initBudgs,
  investments:         IS_DEMO ? demoInvestments   : load('maw_investments',          []),
  investHistory:       IS_DEMO ? demoInvestHistory : load('maw_invest_history',       []),
  // Alocação alvo por tipo de ativo ({tipo: %}, soma 100) e meta de renda passiva mensal (R$)
  investAllocation:    IS_DEMO ? { 'CDB': 40, 'Tesouro Direto': 30, 'FII': 30 } : load('maw_invest_allocation', null),
  passiveIncomeGoal:   IS_DEMO ? 100 : load('maw_passive_goal', 0),
  debts:               IS_DEMO ? demoDebts         : load('maw_debts',                []),
  vehicles:            IS_DEMO ? demoVehicles          : load('maw_vehicles',             []),
  vehicleMaintenance:  IS_DEMO ? demoVehicleMaintenance : load('maw_vehicle_maintenance',  []),
  privacyMode:         load('maw_privacy', false),
  theme:               load('maw_theme', 'default'),
  notifications:       IS_DEMO ? []                : load('maw_notifications',        []),
  activeTab:           'dashboard',
  viewMonth:           null, // null = mês atual; 'YYYY-MM' quando o usuário navega

  darkMode:            load('maw_darkMode', false),
  user:                IS_DEMO ? demoUser          : load('maw_user',                 null),
  credentials:         IS_DEMO ? null              : load('maw_credentials',          null),
  license:             IS_DEMO ? { key: 'DEMO', plan: 'pro', valid: true } : loadLicense(),
  cloudLoaded:          IS_DEMO,
  isDemo:               IS_DEMO,
  savingsGoalMonthly:   IS_DEMO ? 3500 : load('maw_savings_goal_monthly', 0),
  familyId:             IS_DEMO ? null : load('maw_family_id', null),
  salaryDivision:       IS_DEMO ? null : load('maw_salary_division', null),
  // Cotação USD→BRL pra contas em dólar — cache local na primeira renderização,
  // atualizada em background no boot (não é salva na nuvem)
  usdRate:              getCachedUsdRate(),
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_TAB': return { ...state, activeTab: action.payload };
    case 'SET_USD_RATE': return { ...state, usdRate: action.payload };
    case 'TOGGLE_DARK': return { ...state, darkMode: !state.darkMode };
    case 'TOGGLE_PRIVACY': return { ...state, privacyMode: !state.privacyMode };
    case 'SET_VIEW_MONTH': return { ...state, viewMonth: action.payload };
    case 'LOAD_CLOUD_DATA': {
      const d = action.payload;
      // Categories: merge cloud + local so offline-created ones are never lost
      const cloudCats = d.categories ?? state.categories;
      const cloudCatIds = new Set(cloudCats.map(c => c.id));
      const mergedCats = migrateCategories([...cloudCats, ...state.categories.filter(c => !cloudCatIds.has(c.id))]);
      // License: prefer cloud, fallback to local — NUNCA aceita licença DEMO
      const rawLicense = d.license?.key === 'DEMO' ? null : (d.license ?? null);
      const cloudLicense = rawLicense ?? (state.license?.key === 'DEMO' ? null : state.license);
      if (cloudLicense?.key) {
        try { localStorage.setItem('maw_license', JSON.stringify(cloudLicense)); } catch {}
        try { localStorage.setItem('maw_k', cloudLicense.key); } catch {}
      }
      const { categories: finalCats, transactions: finalTxs, budgets: finalBudgets } =
        migrateRemoveBancario(mergedCats, d.transactions ?? state.transactions, d.budgets ?? state.budgets);
      return {
        ...state,
        accounts:           d.accounts           ?? state.accounts,
        cards:              d.cards              ?? state.cards,
        categories:         finalCats,
        transactions:       finalTxs,
        transfers:          d.transfers          ?? state.transfers,
        goals:              d.goals              ?? state.goals,
        budgets:            finalBudgets,
        investments:        d.investments        ?? state.investments,
        investHistory:      d.investHistory      ?? state.investHistory,
        investAllocation:   d.investAllocation   ?? state.investAllocation,
        passiveIncomeGoal:  d.passiveIncomeGoal  ?? state.passiveIncomeGoal,
        debts:              d.debts              ?? state.debts,
        vehicles:           d.vehicles           ?? state.vehicles,
        vehicleMaintenance: d.vehicleMaintenance ?? state.vehicleMaintenance,
        notifications:      d.notifications      ?? state.notifications,
        salaryDivision:     d.salaryDivision      ?? state.salaryDivision,
        license:            cloudLicense,
        cloudLoaded:        true,
      };
    }
    case 'SET_CLOUD_LOADED': return { ...state, cloudLoaded: true };
    case 'SET_THEME': return { ...state, theme: action.payload };
    case 'SET_SAVINGS_GOAL': return { ...state, savingsGoalMonthly: action.payload };
    case 'SET_FAMILY': return { ...state, familyId: action.payload };
    case 'SET_SALARY_DIVISION': return { ...state, salaryDivision: action.payload };
    case 'SET_INVEST_ALLOCATION': return { ...state, investAllocation: action.payload };
    case 'SET_PASSIVE_GOAL': return { ...state, passiveIncomeGoal: action.payload };
    case 'SET_USER': {
      // Keep licenseKey in credentials for backup, but no longer store password
      const licenseKey = state.license?.key || state.credentials?.licenseKey;
      const creds = action.payload
        ? { email: action.payload.email, licenseKey: licenseKey || undefined }
        : state.credentials;
      return { ...state, user: action.payload, credentials: creds || null };
    }
    case 'SET_LICENSE': return { ...state, license: action.payload };
    case 'LOGOUT': return { ...state, user: null, cloudLoaded: false };
    case 'CLEAR_ALL_DATA':
      return {
        ...state,
        transactions: [],
        accounts: [],
        cards: [],
        goals: [],
        budgets: [],
        investments: [],
        investHistory: [],
        transfers: [],
        notifications: [],
        debts: [],
        vehicles: [],
        vehicleMaintenance: [],
        savingsGoalMonthly: 0,
        salaryDivision: null,
        categories: initialCategories,
        activeTab: 'dashboard',
      };

    case 'ADD_TRANSACTION': {
      const tx = { id: uuidv4(), ...action.payload };
      return { ...state, transactions: [tx, ...state.transactions], accounts: applyTxToBalance(state.accounts, tx, +1), cards: applyTxToCard(state.cards, tx, +1) };
    }
    // Transações que chegaram da nuvem em tempo real (WhatsApp/Telegram ou outro
    // aparelho) e que o app ainda não tinha. Aplica saldo/cartão como um ADD normal.
    case 'MERGE_REMOTE_TRANSACTIONS': {
      const existingIds = new Set(state.transactions.map(t => t.id));
      const news = (action.payload || []).filter(t => t && t.id && !existingIds.has(t.id));
      if (!news.length) return state;
      const accs = news.reduce((acc, tx) => applyTxToBalance(acc, tx, +1), state.accounts);
      const crds = news.reduce((c, tx) => applyTxToCard(c, tx, +1), state.cards);
      return { ...state, transactions: [...news, ...state.transactions], accounts: accs, cards: crds };
    }
    case 'ADD_TRANSACTION_SERIES': {
      // id sempre gerado aqui — payloads podem trazer id: undefined que sobrescreveria o uuid
      const series = action.payload.map(p => ({ ...p, id: uuidv4() }));
      const accs = series.reduce((acc, tx) => applyTxToBalance(acc, tx, +1), state.accounts);
      const crds = series.reduce((c, tx) => applyTxToCard(c, tx, +1), state.cards);
      return { ...state, transactions: [...series, ...state.transactions], accounts: accs, cards: crds };
    }
    case 'UPDATE_TRANSACTION': {
      const old = state.transactions.find(t => t.id === action.payload.id);
      const nw = action.payload;
      let accs = state.accounts;
      let crds = state.cards;
      if (old) { accs = applyTxToBalance(accs, old, -1); crds = applyTxToCard(crds, old, -1); }
      accs = applyTxToBalance(accs, nw, +1);
      crds = applyTxToCard(crds, nw, +1);
      return { ...state, transactions: state.transactions.map(t => t.id === nw.id ? nw : t), accounts: accs, cards: crds };
    }
    // Migração v1.7.2: investimentos em USD passaram a guardar invested/current/
    // dividendsReceived em DÓLAR (antes era R$ convertido). Divide os valores
    // legados pelo câmbio e marca usdV2 — roda uma vez por ativo.
    case 'MIGRATE_USD_INVESTMENTS': {
      const rate = action.payload;
      if (!(rate > 0)) return state;
      let changed = false;
      const migrated = state.investments.map(inv => {
        if (inv.currency !== 'USD' || inv.usdV2) return inv;
        changed = true;
        return {
          ...inv,
          usdV2: true,
          invested: round2((inv.invested || 0) / rate),
          current: round2((inv.current || 0) / rate),
          dividendsReceived: round2((inv.dividendsReceived || 0) / rate),
        };
      });
      return changed ? { ...state, investments: migrated } : state;
    }

    // Fotografia mensal da carteira de investimentos — base dos gráficos de
    // evolução. Substitui o retrato do mês corrente; meses passados ficam
    // congelados como fechamento.
    case 'RECORD_INVEST_SNAPSHOT': {
      const snap = action.payload;
      const existing = (state.investHistory || []).find(h => h.month === snap.month);
      if (existing && existing.invested === snap.invested && existing.current === snap.current) return state;
      const hist = [...(state.investHistory || []).filter(h => h.month !== snap.month), snap]
        .sort((a, b) => a.month.localeCompare(b.month))
        .slice(-120);
      return { ...state, investHistory: hist };
    }

    // Marca/desmarca "assinatura" na série inteira — a intenção do usuário é
    // "Netflix é uma assinatura", não "só a parcela de julho é assinatura"
    case 'SET_GROUP_SUBSCRIPTION': {
      const { groupId, value } = action.payload;
      return { ...state, transactions: state.transactions.map(t => t.recurrentGroupId === groupId ? { ...t, isSubscription: value } : t) };
    }
    case 'DELETE_TRANSACTION': {
      const tx = state.transactions.find(t => t.id === action.payload);
      return { ...state, transactions: state.transactions.filter(t => t.id !== action.payload), accounts: tx ? applyTxToBalance(state.accounts, tx, -1) : state.accounts, cards: tx ? applyTxToCard(state.cards, tx, -1) : state.cards };
    }
    case 'TOGGLE_TX_STATUS': {
      const tx = state.transactions.find(t => t.id === action.payload);
      if (!tx) return state;
      const toggled = { ...tx, status: tx.status === 'paid' ? 'pending' : 'paid' };
      // Parcela vinculada a um investimento (ex: capitalização, consórcio): pagar a
      // parcela soma o valor ao que já foi investido — é dinheiro guardado, não gasto
      const investments = tx.investmentId
        ? state.investments.map(inv => inv.id === tx.investmentId
            ? { ...inv, invested: inv.invested + (toggled.status === 'paid' ? tx.value : -tx.value), current: inv.current + (toggled.status === 'paid' ? tx.value : -tx.value) }
            : inv)
        : state.investments;
      // Transações de cartão: saldo bancário e card.used só mudam via PAY_CARD_BILL
      if (tx.cardId) {
        return { ...state, transactions: state.transactions.map(t => t.id === action.payload ? toggled : t), investments };
      }
      let accs = state.accounts;
      if (tx.status === 'paid') { accs = applyTxToBalance(accs, tx, -1); }
      else { accs = applyTxToBalance(accs, toggled, +1); }
      return { ...state, transactions: state.transactions.map(t => t.id === action.payload ? toggled : t), accounts: accs, investments };
    }
    case 'IMPORT_TRANSACTIONS': {
      const imported = action.payload.map(p => ({ id: uuidv4(), ...p }));
      const accs = imported.reduce((acc, tx) => applyTxToBalance(acc, tx, +1), state.accounts);
      const crds = imported.reduce((c, tx) => applyTxToCard(c, tx, +1), state.cards);
      return { ...state, transactions: [...imported, ...state.transactions], accounts: accs, cards: crds };
    }

    case 'PAY_CARD_BILL': {
      const { cardId, amount, accountId, date } = action.payload;
      // Cria transação de pagamento na conta bancária
      const payTx = { id: uuidv4(), description: 'Pagamento de fatura', value: amount, type: 'expense', accountId, cardId: '', date, status: 'paid', categoryId: 'cartao', recurrent: false, notes: 'Pagamento de fatura do cartão' };
      const accs = applyTxToBalance(state.accounts, payTx, +1);
      // Zera o used do cartão pelo valor pago
      const crds = state.cards.map(c => c.id === cardId ? { ...c, used: Math.max(0, round2((c.used || 0) - amount)) } : c);
      // Marca como pagas só as parcelas já vencidas/de hoje pra trás — nunca parcelas
      // futuras (senão uma fatura paga hoje quitaria por engano anos de parcelas
      // recorrentes futuras, tipo capitalização/assinatura, que ainda nem venceram).
      const todayStr = new Date().toISOString().slice(0, 10);
      // ...com uma exceção: se TODAS as compras em aberto ainda estão no futuro,
      // é porque a fatura está sendo paga ADIANTADO (antes do vencimento) — aí a
      // baixa vale para a fatura mais próxima, senão o pagamento não daria baixa
      // em nada. As faturas seguintes continuam pendentes.
      const pendingDates = state.transactions
        .filter(t => t.cardId === cardId && t.status === 'pending' && t.date)
        .map(t => t.date)
        .sort();
      const limitStr = (pendingDates.length && pendingDates[0] > todayStr) ? pendingDates[0] : todayStr;
      let investments = state.investments;
      const txs = state.transactions.map(t => {
        if (t.cardId !== cardId || t.status !== 'pending' || t.date > limitStr) return t;
        // Parcela vinculada a um investimento (capitalização/consórcio): soma ao investido
        if (t.investmentId) {
          investments = investments.map(inv => inv.id === t.investmentId
            ? { ...inv, invested: round2(inv.invested + t.value), current: round2(inv.current + t.value) }
            : inv);
        }
        return { ...t, status: 'paid' };
      });
      return { ...state, transactions: [payTx, ...txs], accounts: accs, cards: crds, investments };
    }

    case 'ADD_ACCOUNT':
      return { ...state, accounts: [...state.accounts, { id: uuidv4(), ...action.payload }] };
    case 'UPDATE_ACCOUNT':
      return { ...state, accounts: state.accounts.map(a => a.id === action.payload.id ? action.payload : a) };
    case 'DELETE_ACCOUNT':
      return {
        ...state,
        accounts: state.accounts.filter(a => a.id !== action.payload),
        transactions: state.transactions.filter(t => t.accountId !== action.payload),
        transfers: state.transfers.filter(t => t.fromId !== action.payload && t.toId !== action.payload),
      };

    case 'ADD_TRANSFER': {
      // toValue: valor creditado no destino quando as moedas diferem (ex: sai
      // R$ 100 do Bradesco, chegam US$ 18,20 na Nomad). Ausente = mesma moeda.
      const { fromId, toId, value, toValue, date, description } = action.payload;
      const credited = toValue ?? value;
      const transfer = { id: uuidv4(), fromId, toId, value, toValue: credited, date, description };
      const updatedAccounts = state.accounts.map(a => {
        if (a.id === fromId) return { ...a, balance: round2(a.balance - value) };
        if (a.id === toId) return { ...a, balance: round2(a.balance + credited) };
        return a;
      });
      return { ...state, transfers: [transfer, ...state.transfers], accounts: updatedAccounts };
    }

    case 'ADD_CARD':
      return { ...state, cards: [...state.cards, { id: uuidv4(), ...action.payload }] };
    case 'UPDATE_CARD':
      return { ...state, cards: state.cards.map(c => c.id === action.payload.id ? action.payload : c) };
    case 'DELETE_CARD':
      return { ...state, cards: state.cards.filter(c => c.id !== action.payload) };

    case 'ADD_GOAL':
      return { ...state, goals: [...state.goals, { id: uuidv4(), ...action.payload }] };
    case 'UPDATE_GOAL':
      return { ...state, goals: state.goals.map(g => g.id === action.payload.id ? action.payload : g) };
    case 'DELETE_GOAL':
      return { ...state, goals: state.goals.filter(g => g.id !== action.payload) };

    case 'ADD_CATEGORY':
      return { ...state, categories: [...state.categories, { id: uuidv4(), ...action.payload }] };
    case 'UPDATE_CATEGORY':
      return { ...state, categories: state.categories.map(c => c.id === action.payload.id ? action.payload : c) };
    case 'DELETE_CATEGORY':
      return { ...state, categories: state.categories.filter(c => c.id !== action.payload), budgets: state.budgets.filter(b => b.categoryId !== action.payload) };

    case 'ADD_BUDGET':
      return { ...state, budgets: [...state.budgets, { id: uuidv4(), ...action.payload }] };
    case 'UPDATE_BUDGET':
      return { ...state, budgets: state.budgets.map(b => b.id === action.payload.id ? action.payload : b) };
    case 'DELETE_BUDGET':
      return { ...state, budgets: state.budgets.filter(b => b.id !== action.payload) };

    case 'ADD_INVESTMENT':
      return { ...state, investments: [...state.investments, { id: uuidv4(), ...action.payload }] };
    case 'UPDATE_INVESTMENT':
      return { ...state, investments: state.investments.map(i => i.id === action.payload.id ? action.payload : i) };
    case 'DELETE_INVESTMENT':
      return { ...state, investments: state.investments.filter(i => i.id !== action.payload) };

    case 'ADD_DEBT':
      return { ...state, debts: [...state.debts, action.payload] };
    case 'UPDATE_DEBT':
      return { ...state, debts: state.debts.map(d => d.id === action.payload.id ? action.payload : d) };
    case 'DELETE_DEBT':
      return { ...state, debts: state.debts.filter(d => d.id !== action.payload) };

    case 'ADD_NOTIFICATION':
      return { ...state, notifications: [{ id: uuidv4(), read: false, createdAt: new Date().toISOString(), ...action.payload }, ...state.notifications].slice(0, 50) };
    case 'MARK_NOTIF_READ':
      return { ...state, notifications: state.notifications.map(n => n.id === action.payload ? { ...n, read: true } : n) };
    case 'CLEAR_NOTIFICATIONS':
      return { ...state, notifications: state.notifications.map(n => ({ ...n, read: true })) };
    case 'DELETE_ALL_NOTIFICATIONS':
      return { ...state, notifications: [] };

    case 'ADD_VEHICLE':
      return { ...state, vehicles: [...state.vehicles, { id: uuidv4(), ...action.payload }] };
    case 'UPDATE_VEHICLE':
      return { ...state, vehicles: state.vehicles.map(v => v.id === action.payload.id ? action.payload : v) };
    case 'DELETE_VEHICLE':
      return { ...state, vehicles: state.vehicles.filter(v => v.id !== action.payload), vehicleMaintenance: state.vehicleMaintenance.filter(m => m.vehicleId !== action.payload) };
    case 'ADD_MAINTENANCE':
      return { ...state, vehicleMaintenance: [{ id: uuidv4(), ...action.payload }, ...state.vehicleMaintenance] };
    case 'UPDATE_MAINTENANCE':
      return { ...state, vehicleMaintenance: state.vehicleMaintenance.map(m => m.id === action.payload.id ? action.payload : m) };
    case 'DELETE_MAINTENANCE':
      return { ...state, vehicleMaintenance: state.vehicleMaintenance.filter(m => m.id !== action.payload) };

    default: return state;
  }
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Flag síncrono (não em useEffect): os filhos renderizam depois do provider,
  // então formatCurrency já enxerga o valor novo no mesmo ciclo de render
  setPrivacyMode(state.privacyMode);

  useEffect(() => { if (!IS_DEMO) localStorage.setItem('maw_accounts',      JSON.stringify(state.accounts));      }, [state.accounts]);
  useEffect(() => { if (!IS_DEMO) localStorage.setItem('maw_cards',         JSON.stringify(state.cards));         }, [state.cards]);
  useEffect(() => { if (!IS_DEMO) localStorage.setItem('maw_categories',    JSON.stringify(state.categories));    }, [state.categories]);
  useEffect(() => { if (!IS_DEMO) localStorage.setItem('maw_transactions',  JSON.stringify(state.transactions));  }, [state.transactions]);
  useEffect(() => { if (!IS_DEMO) localStorage.setItem('maw_transfers',     JSON.stringify(state.transfers));     }, [state.transfers]);
  useEffect(() => { if (!IS_DEMO) localStorage.setItem('maw_goals',         JSON.stringify(state.goals));         }, [state.goals]);
  useEffect(() => { if (!IS_DEMO) localStorage.setItem('maw_budgets',       JSON.stringify(state.budgets));       }, [state.budgets]);
  useEffect(() => { if (!IS_DEMO) localStorage.setItem('maw_investments',   JSON.stringify(state.investments));   }, [state.investments]);
  useEffect(() => { if (!IS_DEMO) localStorage.setItem('maw_invest_history', JSON.stringify(state.investHistory)); }, [state.investHistory]);
  useEffect(() => { if (!IS_DEMO) localStorage.setItem('maw_invest_allocation', JSON.stringify(state.investAllocation)); }, [state.investAllocation]);
  useEffect(() => { if (!IS_DEMO) localStorage.setItem('maw_passive_goal',      JSON.stringify(state.passiveIncomeGoal)); }, [state.passiveIncomeGoal]);

  // Migração única: ativos USD legados (valores em R$) → valores em US$ (usdV2)
  useEffect(() => {
    if (!state.cloudLoaded) return;
    if (!state.investments.some(i => i.currency === 'USD' && !i.usdV2)) return;
    dispatch({ type: 'MIGRATE_USD_INVESTMENTS', payload: state.usdRate });
  }, [state.cloudLoaded, state.investments, state.usdRate]); // eslint-disable-line

  // Grava a fotografia do mês corrente da carteira sempre que os investimentos
  // mudam (só depois do cloudLoaded, pra não fotografar o estado vazio do boot).
  // Ativos em US$ entram convertidos pra R$ — espera a migração usdV2 rodar.
  useEffect(() => {
    if (!state.cloudLoaded) return;
    if (state.investments.length === 0 && (state.investHistory || []).length === 0) return;
    if (state.investments.some(i => i.currency === 'USD' && !i.usdV2)) return;
    const inBRL = (v, i) => i.currency === 'USD' ? v * state.usdRate : v;
    const now = new Date();
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const invested = round2(state.investments.reduce((s, i) => s + inBRL(i.invested || 0, i), 0));
    const current = round2(state.investments.reduce((s, i) => s + inBRL(i.current || i.invested || 0, i), 0));
    const byType = {};
    state.investments.forEach(i => { byType[i.type] = round2((byType[i.type] || 0) + inBRL(i.current || i.invested || 0, i)); });
    dispatch({ type: 'RECORD_INVEST_SNAPSHOT', payload: { month, invested, current, byType } });
  }, [state.cloudLoaded, state.investments]); // eslint-disable-line
  useEffect(() => { if (!IS_DEMO) localStorage.setItem('maw_debts',                JSON.stringify(state.debts));               }, [state.debts]);
  useEffect(() => { if (!IS_DEMO) localStorage.setItem('maw_vehicles',            JSON.stringify(state.vehicles));            }, [state.vehicles]);
  useEffect(() => { if (!IS_DEMO) localStorage.setItem('maw_vehicle_maintenance', JSON.stringify(state.vehicleMaintenance));  }, [state.vehicleMaintenance]);
  useEffect(() => { if (!IS_DEMO) localStorage.setItem('maw_notifications',       JSON.stringify(state.notifications));       }, [state.notifications]);
  useEffect(() => { if (!IS_DEMO) localStorage.setItem('maw_darkMode',      JSON.stringify(state.darkMode));      }, [state.darkMode]);
  useEffect(() => { if (!IS_DEMO) localStorage.setItem('maw_privacy',       JSON.stringify(state.privacyMode));   }, [state.privacyMode]);
  useEffect(() => { if (!IS_DEMO) localStorage.setItem('maw_user',          JSON.stringify(state.user));          }, [state.user]);
  useEffect(() => { if (!IS_DEMO) localStorage.setItem('maw_credentials',         JSON.stringify(state.credentials));         }, [state.credentials]);
  useEffect(() => { if (!IS_DEMO) localStorage.setItem('maw_savings_goal_monthly', JSON.stringify(state.savingsGoalMonthly)); }, [state.savingsGoalMonthly]);
  useEffect(() => { if (!IS_DEMO) localStorage.setItem('maw_family_id',            JSON.stringify(state.familyId));            }, [state.familyId]);
  useEffect(() => { if (!IS_DEMO) localStorage.setItem('maw_salary_division',      JSON.stringify(state.salaryDivision));      }, [state.salaryDivision]);

  // Apply dark mode class to document
  useEffect(() => {
    document.documentElement.classList.toggle('dark', state.darkMode);
  }, [state.darkMode]);

  useEffect(() => {
    const el = document.documentElement;
    el.classList.forEach(c => { if (c.startsWith('theme-')) el.classList.remove(c); });
    if (state.theme && state.theme !== 'default') el.classList.add(`theme-${state.theme}`);
    localStorage.setItem('maw_theme', JSON.stringify(state.theme));
  }, [state.theme]);

  // Version-based system notification
  useEffect(() => {
    const lastVersion = localStorage.getItem('maw_app_version');
    if (lastVersion && lastVersion !== APP_VERSION) {
      dispatch({ type: 'ADD_NOTIFICATION', payload: {
        title: `MAW Finanças atualizado — v${APP_VERSION}`,
        message: `Bem-vindo à versão ${APP_VERSION}! Manutenção de Residência (não só veículos) e sincronização em tempo real — veja tudo na tela Novidades (⭐).`,
        type: 'info',
      }});
    }
    localStorage.setItem('maw_app_version', APP_VERSION);
  }, []); // eslint-disable-line

  // Verifica contas próximas do vencimento (até alertDays dias)
  // Roda quando cloudLoaded muda: garante que os dados da nuvem já estão disponíveis
  useEffect(() => {
    if (state.user && !state.cloudLoaded) return;

    const alertDays = state.prefs?.alertDays ?? 3;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const inXdays = new Date(today); inXdays.setDate(inXdays.getDate() + alertDays);
    const notifiedIds = new Set(state.notifications.filter(n => n.txId).map(n => n.txId));
    const pending = state.transactions.filter(t => t.status === 'pending' && t.type === 'expense');
    pending.forEach(t => {
      const due = new Date(t.date + 'T00:00:00');
      if (!notifiedIds.has(t.id) && due <= inXdays && due >= today) {
        const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24));
        const dueLabel = diffDays === 0 ? 'hoje' : diffDays === 1 ? 'amanhã' : `em ${due.toLocaleDateString('pt-BR')}`;
        const valor = t.value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        dispatch({ type: 'ADD_NOTIFICATION', payload: { txId: t.id, title: 'Conta próxima do vencimento', message: `${t.description} vence ${dueLabel} — R$ ${valor}`, type: 'warning' } });
      }
    });
  }, [state.cloudLoaded]); // eslint-disable-line

  // Auto-gera lançamentos recorrentes mensais quando uma série expirou
  const recurCheckedRef = useRef(false);
  useEffect(() => {
    if (state.user && !state.cloudLoaded) return;
    if (recurCheckedRef.current) return;
    recurCheckedRef.current = true;

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const curYear = today.getFullYear();
    const curMonth = today.getMonth();

    // Agrupa lançamentos por recurrentGroupId
    const groups = {};
    state.transactions.forEach(t => {
      if (!t.recurrentGroupId) return;
      if (!groups[t.recurrentGroupId]) groups[t.recurrentGroupId] = [];
      groups[t.recurrentGroupId].push(t);
    });

    const toAdd = [];
    Object.values(groups).forEach(txs => {
      const template = txs[0];
      if ((template.frequency || 'monthly') !== 'monthly') return;

      const sorted = [...txs].sort((a, b) => new Date(a.date) - new Date(b.date));
      const latest = sorted[sorted.length - 1];
      const latestDate = new Date(latest.date + 'T00:00:00');

      // Se o último lançamento já é do mês atual ou futuro, nada a fazer
      if (latestDate.getFullYear() > curYear ||
          (latestDate.getFullYear() === curYear && latestDate.getMonth() >= curMonth)) return;

      // Gera os meses faltantes até o mês atual, preservando o dia do mês
      // (com clamp para meses curtos: dia 31 vira 28/29 em fevereiro, sem escorregar de mês)
      const baseDay = latestDate.getDate();
      for (let i = 1; i <= 24; i++) {
        const y = latestDate.getFullYear();
        const m = latestDate.getMonth() + i;
        const target = new Date(y, m, 1);
        if (target.getFullYear() > curYear || (target.getFullYear() === curYear && target.getMonth() > curMonth)) break;
        const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
        target.setDate(Math.min(baseDay, lastDay));
        const dateStr = `${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, '0')}-${String(target.getDate()).padStart(2, '0')}`;
        const monthStr = dateStr.slice(0, 7);
        if (!txs.some(t => t.date.startsWith(monthStr))) {
          const { id, ...rest } = latest;
          toAdd.push({ ...rest, date: dateStr, status: 'pending', autoGenerated: true });
        }
      }
    });

    if (toAdd.length > 0) {
      dispatch({ type: 'ADD_TRANSACTION_SERIES', payload: toAdd });
      dispatch({ type: 'ADD_NOTIFICATION', payload: {
        title: 'Recorrências geradas automaticamente',
        message: `${toAdd.length} lançamento${toAdd.length !== 1 ? 's' : ''} recorrente${toAdd.length !== 1 ? 's' : ''} adicionado${toAdd.length !== 1 ? 's' : ''} automaticamente.`,
        type: 'info',
      }});
    }
  }, [state.cloudLoaded]); // eslint-disable-line

  // Notifica manutenções de veículos com nextDate próxima (30 dias)
  const maintNotifRef = useRef(false);
  useEffect(() => {
    if (state.user && !state.cloudLoaded) return;
    if (maintNotifRef.current) return;
    maintNotifRef.current = true;

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const in30 = new Date(today); in30.setDate(in30.getDate() + 30);
    const notifiedIds = new Set(state.notifications.filter(n => n.maintId).map(n => n.maintId));

    state.vehicleMaintenance.forEach(m => {
      if (!m.nextDate) return;
      if (notifiedIds.has(m.id)) return;
      const next = new Date(m.nextDate + 'T00:00:00');
      if (next >= today && next <= in30) {
        const diffDays = Math.round((next - today) / (1000 * 60 * 60 * 24));
        const label = diffDays === 0 ? 'hoje' : diffDays === 1 ? 'amanhã' : `em ${next.toLocaleDateString('pt-BR')}`;
        const vehicle = state.vehicles.find(v => v.id === m.vehicleId);
        const vName = vehicle ? vehicle.name : 'Bem';
        const kindLabel = vehicle?.kind === 'residence' ? 'residência' : 'veículo';
        dispatch({ type: 'ADD_NOTIFICATION', payload: {
          maintId: m.id,
          title: `Manutenção de ${kindLabel} — ${vName}`,
          message: `${m.description} programada para ${label}.`,
          type: 'warning',
        }});
      }
    });
  }, [state.cloudLoaded]); // eslint-disable-line

  // Assinatura Kiwify: verifica status na nuvem a cada boot logado
  // e trata expiração de licenças com validade
  const subCheckedRef = useRef(false);
  useEffect(() => {
    if (IS_DEMO) return;

    // 1. Licença local expirada → remove e avisa
    if (state.license && isLicenseExpired(state.license)) {
      dispatch({ type: 'SET_LICENSE', payload: null });
      try { localStorage.removeItem('maw_license'); localStorage.removeItem('maw_k'); } catch {}
      dispatch({ type: 'ADD_NOTIFICATION', payload: {
        title: 'Assinatura expirada',
        message: 'Sua assinatura venceu. Renove para continuar com acesso completo.',
        type: 'warning',
      }});
      return;
    }

    // 2. Aviso de renovação (≤ 5 dias) — uma notificação por data de vencimento
    if (state.license?.expiresAt) {
      const days = Math.ceil((new Date(state.license.expiresAt) - Date.now()) / 86400000);
      const warnKey = `renew-${state.license.expiresAt}`;
      const alreadyWarned = state.notifications.some(n => n.renewKey === warnKey);
      if (days <= 5 && days > 0 && !alreadyWarned) {
        dispatch({ type: 'ADD_NOTIFICATION', payload: {
          renewKey: warnKey,
          title: 'Assinatura perto de vencer',
          message: `Seu plano ${PLAN_LABEL[state.license.plan] || ''} vence em ${days} dia${days !== 1 ? 's' : ''}. A renovação pela Kiwify é automática se a cobrança estiver ativa.`,
          type: 'info',
        }});
      }
    }

    // 3. Consulta o status da assinatura na nuvem (uma vez por sessão, após login)
    if (subCheckedRef.current) return;
    const email = state.user?.email;
    if (!email || (state.user && !state.cloudLoaded)) return;
    subCheckedRef.current = true;

    loadSubscription(email).then(sub => {
      if (!sub || !sub.plan) return;
      const active = sub.status === 'active' && sub.expiresAt && new Date(sub.expiresAt) > new Date();
      const cur = state.license;
      // Licença vitalícia por chave nunca é rebaixada pela assinatura
      const isLifetime = cur && !cur.expiresAt && cur.source !== 'subscription';
      if (active && !isLifetime) {
        const lic = { plan: sub.plan, source: 'subscription', expiresAt: sub.expiresAt, activatedAt: new Date().toISOString() };
        dispatch({ type: 'SET_LICENSE', payload: lic });
        try { localStorage.setItem('maw_license', JSON.stringify(lic)); } catch {}
      } else if (!active && cur?.source === 'subscription') {
        // Assinatura cancelada/expirada na nuvem → remove localmente
        dispatch({ type: 'SET_LICENSE', payload: null });
        try { localStorage.removeItem('maw_license'); } catch {}
      }
    });
  }, [state.cloudLoaded, state.license]); // eslint-disable-line

  // Salvamento automático na nuvem — debounce de 2s após qualquer alteração financeira
  const saveTimerRef = useRef(null);
  useEffect(() => {
    if (!state.cloudLoaded) return; // só salva depois que os dados da nuvem foram carregados
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveCloudData(uid, state, state.familyId).catch(e => console.warn('Firestore: falha ao salvar:', e.message));
    }, 2000);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [ // eslint-disable-line
    state.accounts, state.cards, state.categories, state.transactions,
    state.transfers, state.goals, state.budgets, state.investments, state.debts,
    state.vehicles, state.vehicleMaintenance, state.notifications, state.license,
    state.investHistory, state.investAllocation, state.passiveIncomeGoal,
  ]);

  // Sincronização em tempo real: escuta a nuvem e puxa na hora as transações
  // novas que o app não tem (lançamento via WhatsApp/Telegram com o app aberto,
  // ou feito em outro aparelho). Elimina a corrida na origem.
  const txIdsRef = useRef(new Set());
  useEffect(() => { txIdsRef.current = new Set(state.transactions.map(t => t.id)); }, [state.transactions]);
  useEffect(() => {
    if (IS_DEMO || !state.cloudLoaded) return;
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const unsub = subscribeCloudTransactions(
      uid,
      state.familyId,
      () => txIdsRef.current,
      (news) => dispatch({ type: 'MERGE_REMOTE_TRANSACTIONS', payload: news }),
    );
    return unsub;
  }, [state.cloudLoaded, state.familyId]); // eslint-disable-line

  // Atualiza a cotação do dólar em background (contas/transações em USD)
  useEffect(() => {
    fetchUsdRate().then(r => { if (r > 0) dispatch({ type: 'SET_USD_RATE', payload: r }); });
  }, []);

  // Saldos e totais do mês sempre em BRL — contas/transações em USD convertidas
  const totalBalance = sumAccountsBRL(state.accounts, state.usdRate);
  const now = new Date();
  const nowMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  // Mês em visualização: o que o usuário escolheu no seletor, ou o mês atual
  const currentMonth = state.viewMonth || nowMonth;
  const currentMonthTx = state.transactions.filter(t => t.date.startsWith(currentMonth));
  // Exclui transações de cartão (cardId preenchido) dos totais bancários para evitar
  // dupla contagem com o PAY_CARD_BILL que já representa o gasto no banco
  const sumBRL = (txs) => txs.reduce((s, t) => s + toBRL(t.value, t.currency, state.usdRate), 0);
  const monthIncome  = sumBRL(currentMonthTx.filter(t => t.type === 'income'  && t.status === 'paid'    && !t.cardId));
  const monthExpense = sumBRL(currentMonthTx.filter(t => t.type === 'expense' && t.status === 'paid'    && !t.cardId));
  const monthPending = sumBRL(currentMonthTx.filter(t => t.type === 'expense' && t.status === 'pending' && !t.cardId));
  const unreadNotifs = state.notifications.filter(n => !n.read).length;

  return (
    <AppContext.Provider value={{ state, dispatch, totalBalance, monthIncome, monthExpense, monthPending, currentMonth, nowMonth, unreadNotifs }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
